// src/admin/CatechistLeaveRequests.js - Duyệt đơn Realtime & Tính khoảng cách ngày
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { StudentContext } from '../StudentContext';
import { subscribeLeaveRequestsByBlock, updateLeaveRequestStatus } from '../FirebaseService';
import { auth } from '../firebaseConfig';
import './CatechistLeaveRequests.css';

const CatechistLeaveRequests = () => {
  const { currentYearId } = useContext(StudentContext);

  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('pending');
  const [searchTerm, setSearchTerm] = useState('');

  const [selectedReq, setSelectedReq] = useState(null);
  const [actionType, setActionType] = useState('');
  const [reviewerNote, setReviewerNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const userRole = localStorage.getItem('userRole') || 'catechist';
  const blockLeader = localStorage.getItem('blockLeader') || '';
  const isAdmin = userRole === 'admin';

  // LẮNG NGHE ĐƠN THỜI GIAN THỰC (REALTIME)
  useEffect(() => {
    if (!currentYearId) return;
    setLoading(true);

    const targetBlock = isAdmin ? 'all' : blockLeader;
    const unsubscribe = subscribeLeaveRequestsByBlock(currentYearId, targetBlock, (data) => {
      setRequests(data);
      setLoading(false);
    });

    return () => unsubscribe && unsubscribe();
  }, [currentYearId, blockLeader, isAdmin]);

  // Thống kê số lượng đơn
  const counts = useMemo(() => {
    let pending = 0, approved = 0, rejected = 0;
    requests.forEach(r => {
      if (r.status === 'approved') approved++;
      else if (r.status === 'rejected') rejected++;
      else pending++;
    });
    return { all: requests.length, pending, approved, rejected };
  }, [requests]);

  const filteredRequests = useMemo(() => {
    return requests.filter(r => {
      const matchStatus = filterStatus === 'all' || r.status === filterStatus;
      const searchLower = searchTerm.toLowerCase();
      const matchSearch =
        (r.fullName && r.fullName.toLowerCase().includes(searchLower)) ||
        (r.holyName && r.holyName.toLowerCase().includes(searchLower)) ||
        (r.className && r.className.toLowerCase().includes(searchLower)) ||
        (r.requestDate && r.requestDate.includes(searchLower));
      return matchStatus && matchSearch;
    });
  }, [requests, filterStatus, searchTerm]);

  const handleOpenActionModal = (req, type) => {
    setSelectedReq(req);
    setActionType(type);
    setReviewerNote('');
  };

  const handleConfirmAction = async () => {
    if (!selectedReq || !actionType) return;

    setIsSubmitting(true);
    try {
      const currentUser = auth.currentUser;
      const reviewerUid = currentUser ? currentUser.uid : 'ADMIN';

      const res = await updateLeaveRequestStatus(
        currentYearId,
        selectedReq.id,
        actionType,
        reviewerUid,
        reviewerNote.trim()
      );

      if (res.success) {
        alert(`🎉 Đã ${actionType === 'approved' ? 'CHẤP THUẬN' : 'TỪ CHỐI'} đơn xin nghỉ thành công!`);
        setSelectedReq(null);
        setActionType('');
        setReviewerNote('');
      } else {
        alert('❌ ' + (res.message || 'Lỗi khi cập nhật trạng thái đơn.'));
      }
    } catch (err) {
      alert('❌ Lỗi hệ thống: ' + err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Helper tính thời gian tạo đơn & So sánh với Ngày xin nghỉ
  const formatCreatedTimeAndDelta = (createdAt, requestDateStr) => {
    if (!createdAt) return { createdText: 'Chưa rõ', deltaText: '', deltaClass: '' };

    let createdDate;
    if (createdAt.toDate && typeof createdAt.toDate === 'function') {
      createdDate = createdAt.toDate();
    } else if (createdAt.seconds) {
      createdDate = new Date(createdAt.seconds * 1000);
    } else {
      createdDate = new Date(createdAt);
    }

    if (isNaN(createdDate.getTime())) {
      return { createdText: 'Chưa rõ', deltaText: '', deltaClass: '' };
    }

    const timeStr = createdDate.toLocaleTimeString('vi-VN', { hour: '2-digit', minute: '2-digit', hour12: false });
    const dateStr = createdDate.toLocaleDateString('vi-VN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    const createdText = `${timeStr} - ${dateStr}`;

    if (!requestDateStr) return { createdText, deltaText: '', deltaClass: '' };

    const [rYear, rMonth, rDay] = requestDateStr.split('-').map(Number);
    const reqDate = new Date(rYear, rMonth - 1, rDay);

    const cZero = new Date(createdDate.getFullYear(), createdDate.getMonth(), createdDate.getDate());
    const rZero = new Date(reqDate.getFullYear(), reqDate.getMonth(), reqDate.getDate());

    const diffTime = rZero.getTime() - cZero.getTime();
    const diffDays = Math.round(diffTime / (1000 * 3600 * 24));

    let deltaText = '';
    let deltaClass = '';

    if (diffDays > 0) {
      deltaText = `⚡ Gửi trước ${diffDays} ngày`;
      deltaClass = 'clr-delta-early';
    } else if (diffDays === 0) {
      deltaText = '📌 Gửi cùng ngày xin nghỉ';
      deltaClass = 'clr-delta-same';
    } else {
      deltaText = `⚠️ Gửi trễ ${Math.abs(diffDays)} ngày`;
      deltaClass = 'clr-delta-late';
    }

    return { createdText, deltaText, deltaClass };
  };

  const renderAbsentActivitiesText = (acts = []) => {
    const list = [];
    if (acts.includes('mass')) list.push('⛪ Đi Lễ');
    if (acts.includes('teaching')) list.push('📖 Dạy Giáo Lý');
    if (acts.includes('meeting')) list.push('👥 Đi Họp');
    return list.join(' | ') || 'Tất cả hoạt động';
  };

  const renderStatusBadge = (status) => {
    if (status === 'approved') return <span className="clr-badge clr-badge-approved">✓ Đã chấp thuận</span>;
    if (status === 'rejected') return <span className="clr-badge clr-badge-rejected">✕ Từ chối</span>;
    return <span className="clr-badge clr-badge-pending">⏳ Chờ duyệt</span>;
  };

  return (
    <div className="clr-container">
      <div className="clr-header">
        <h2 className="clr-title">
          📝 Duyệt Đơn Xin Nghỉ Giáo Lý Viên
          {blockLeader && !isAdmin && <span className="clr-block-tag">Khối: {blockLeader}</span>}
        </h2>
        <input
          type="text"
          placeholder="Tìm tên GLV, Lớp, Ngày..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="clr-search-input"
        />
      </div>

      <div className="clr-tabs-bar">
        <button
          className={`clr-tab-btn ${filterStatus === 'pending' ? 'active' : ''}`}
          onClick={() => setFilterStatus('pending')}
        >
          ⏳ Chờ Duyệt {counts.pending > 0 && <span className="clr-count-badge red">{counts.pending}</span>}
        </button>
        <button
          className={`clr-tab-btn ${filterStatus === 'approved' ? 'active' : ''}`}
          onClick={() => setFilterStatus('approved')}
        >
          ✓ Đã Chấp Thuận ({counts.approved})
        </button>
        <button
          className={`clr-tab-btn ${filterStatus === 'rejected' ? 'active' : ''}`}
          onClick={() => setFilterStatus('rejected')}
        >
          ✕ Từ Chối ({counts.rejected})
        </button>
        <button
          className={`clr-tab-btn ${filterStatus === 'all' ? 'active' : ''}`}
          onClick={() => setFilterStatus('all')}
        >
          Tất Cả ({counts.all})
        </button>
      </div>

      {loading ? (
        <p className="clr-loading-text">⏳ Đang tải danh sách đơn xin nghỉ...</p>
      ) : filteredRequests.length === 0 ? (
        <div className="clr-empty-box">
          <p>Không có đơn xin nghỉ nào phù hợp với bộ lọc.</p>
        </div>
      ) : (
        <div className="clr-cards-grid">
          {filteredRequests.map((req) => {
            const { createdText, deltaText, deltaClass } = formatCreatedTimeAndDelta(req.createdAt, req.requestDate);

            return (
              <div key={req.id} className="clr-card">
                <div className="clr-card-header">
                  <div className="clr-user-info">
                    <span className="clr-holy-name">{req.holyName || 'Tên Thánh'}</span>
                    <h4 className="clr-full-name">{req.fullName}</h4>
                    <span className="clr-class-name">Lớp: {req.className || 'Chưa xếp lớp'}</span>
                  </div>
                  {renderStatusBadge(req.status)}
                </div>

                <div className="clr-card-body">
                  <p className="clr-info-line">
                    📅 <strong>Ngày xin nghỉ:</strong> <span className="clr-date-highlight">{req.requestDate}</span>
                  </p>
                  <p className="clr-info-line">
                    🕒 <strong>Thời gian gửi:</strong> {createdText}
                  </p>
                  {deltaText && (
                    <p className="clr-info-line">
                      <span className={`clr-delta-badge ${deltaClass}`}>{deltaText}</span>
                    </p>
                  )}
                  <p className="clr-info-line">
                    📌 <strong>Buổi vắng:</strong> {renderAbsentActivitiesText(req.absentActivities)}
                  </p>
                  <p className="clr-info-line">
                    💬 <strong>Lý do:</strong> {req.reason}
                  </p>

                  {req.reviewerNote && (
                    <p className="clr-reviewer-note">
                      📝 <strong>Ghi chú duyệt:</strong> {req.reviewerNote}
                    </p>
                  )}
                </div>

                {req.status === 'pending' && (
                  <div className="clr-card-actions">
                    <button
                      className="clr-btn-approve"
                      onClick={() => handleOpenActionModal(req, 'approved')}
                    >
                      ✓ Chấp Thuận
                    </button>
                    <button
                      className="clr-btn-reject"
                      onClick={() => handleOpenActionModal(req, 'rejected')}
                    >
                      ✕ Từ Chối
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {selectedReq && (
        <div className="clr-modal-overlay">
          <div className="clr-modal-card">
            <h3 className="clr-modal-title">
              {actionType === 'approved' ? '✓ Xác Nhận Chấp Thuận Đơn' : '✕ Xác Nhận Từ Chối Đơn'}
            </h3>

            <div className="clr-modal-info">
              <p><strong>Giáo Lý Viên:</strong> {selectedReq.holyName} {selectedReq.fullName}</p>
              <p><strong>Lớp:</strong> {selectedReq.className}</p>
              <p><strong>Ngày xin nghỉ:</strong> {selectedReq.requestDate}</p>
              <p><strong>Lý do:</strong> {selectedReq.reason}</p>
            </div>

            <div className="clr-form-group">
              <label>Ghi chú phản hồi (Tùy chọn):</label>
              <textarea
                rows="3"
                placeholder="Nhập ghi chú gửi Giáo lý viên..."
                value={reviewerNote}
                onChange={(e) => setReviewerNote(e.target.value)}
                className="clr-textarea"
              />
            </div>

            <div className="clr-modal-actions">
              <button
                type="button"
                onClick={() => setSelectedReq(null)}
                disabled={isSubmitting}
                className="clr-btn-cancel"
              >
                Hủy Bỏ
              </button>
              <button
                type="button"
                onClick={handleConfirmAction}
                disabled={isSubmitting}
                className={`clr-btn-confirm ${actionType}`}
              >
                {isSubmitting ? '⏳ Đang lưu...' : actionType === 'approved' ? 'Đồng Ý Cho Nghỉ' : 'Xác Nhận Từ Chối'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CatechistLeaveRequests;